import {
    ColorValue,
    LayoutChangeEvent,
    Pressable,
    StyleProp,
    StyleSheet,
    View,
    ViewStyle,
} from "react-native";
import React, { use, useCallback, useEffect, useMemo, useState } from "react";
import { useTheme } from "../../hooks/use-theme";
import Icon, { IconName } from "../../components/Icon";
import { useDropdown } from "../../hooks/use-dropdown";
import Text from "../../components/Text";
import { ScreenHeaderManagerContext } from "../../contexts/ScreenHeaderManager";
import Button from "../../components/Button";
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from "react-native-reanimated";
import { useIsScreenSizeMin } from "../../hooks/useIsScreenSizeMin";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useItemsDropdown } from "../../components/Dropdown";

const BUTTON_ANIMATION_DURATION = 300;

// Floor for the nav-bar title auto-shrink. Below ~70% of `titleMedium` (22px) the title stops
// being comfortably readable, so anything longer gets ellipsized rather than shrunk further.
const MIN_LABEL_FONT_SCALE = 0.7;

export type HeaderButtonProps = {
    label: string;
    disabled?: boolean;
    onPress: () => void;
    style?: StyleProp<ViewStyle>;
};

export const HeaderButton: React.FC<HeaderButtonProps> = ({ label, onPress, disabled, style }) => {
    const { colors, fonts } = useTheme();

    return (
        <Pressable onPress={onPress} style={style}>
            <Text
                style={{
                    ...fonts.bodyMedium,
                    color: disabled ? colors.onSurfaceDisabled : colors.body,
                }}
            >
                {label}
            </Text>
        </Pressable>
    );
};

export type MenuAction = {
    label: string;
    icon: IconName;
    color?: string;
    style?: StyleProp<ViewStyle>;
    ref?: React.RefObject<View | null>;
    onPress: () => void;
    testID?: string;
};

type ActionButtonProps = {
    icon: IconName;
    onPress: () => void;
    bordered?: boolean;
    testID?: string;
    ref?: React.RefObject<View | null>;
    disabled?: boolean;
};

export const ActionButton = ({
    icon,
    onPress,
    bordered,
    testID,
    disabled,
    ref,
}: ActionButtonProps & { ref?: React.Ref<View> }) => {
    const { colors, gridSize } = useTheme();
    const styles = useStyles(false);
    return (
        // collapsable must be set to false to be able to use measureInWindow ( see use-dropdown )
        // see: https://github.com/facebook/react-native/issues/29712
        <View ref={ref} style={{ opacity: 1 }} collapsable={false}>
            <Pressable
                onPress={disabled ? undefined : onPress}
                testID={testID}
                id={testID}
                disabled={disabled}
                style={[
                    {
                        alignItems: "center",
                        justifyContent: "center",
                        flex: 1,
                        flexGrow: 10,
                        flexShrink: 0,
                        width: gridSize * 2,
                    },
                ]}
            >
                <Icon
                    name={icon}
                    size={24}
                    style={[
                        { height: 24, width: 24, flexDirection: "row", alignItems: "center" },
                        bordered ? styles.headerButtonBordered : {},
                    ]}
                    color={disabled ? colors.onSurfaceDisabled : colors.primary}
                />
            </Pressable>
        </View>
    );
};

export type ScreenHeaderProps = {
    label?: string;
    component?: React.ReactNode;
    subLabel?: string;
    actions?: MenuAction[];
    leftIcon?: IconName;
    onLeftAction?: () => void;
    leftActionDisabled?: boolean;
    // Free-form left slot, for a text action (e.g. "Cancel") that would not fit the icon-sized
    // `leftIcon` pressable. Takes precedence over `leftIcon`/`onLeftAction` when set.
    leftComponent?: React.ReactNode;
    useSmallLabel?: boolean;
    useHeaderButton?: boolean;
    id?: string;
    backgroundColor?: ColorValue;
    actionButtonWithBadge?: boolean;
    // Drop the top safe-area inset — for a header already inside a presented native modal (which is
    // inset from the top itself), so the device inset isn't added a second time.
    disableTopInset?: boolean;
};

const ScreenHeader: React.FC<ScreenHeaderProps> = ({
    label,
    component,
    subLabel,
    actions = [],
    onLeftAction,
    leftIcon,
    leftActionDisabled,
    leftComponent,
    useSmallLabel = false,
    useHeaderButton = false,
    id,
    backgroundColor,
    actionButtonWithBadge = false,
    disableTopInset = false,
}) => {
    const styles = useStyles(disableTopInset);
    const { dynamicButtonConfig, isScreenScrolled } = use(ScreenHeaderManagerContext);
    const isLarge = useIsScreenSizeMin("large");

    const [headerHeight, setHeaderHeight] = useState(0);

    // Values when isScreenScrolled = [false, true]
    const animationButtonTop = useMemo(() => [headerHeight, 0], [headerHeight]);

    const top = useSharedValue(animationButtonTop[+isScreenScrolled]);

    useEffect(() => {
        top.value = withTiming(animationButtonTop[+isScreenScrolled], {
            duration: BUTTON_ANIMATION_DURATION,
        });
    }, [animationButtonTop, isScreenScrolled, top]);

    // reanimated 4.3 returns an opaque AnimatedStyleHandle; cast for the non-animated Button style prop.
    const animatedButtonStyles = useAnimatedStyle(() => ({
        top: top.value,
    })) as unknown as StyleProp<ViewStyle>;

    const { colors } = useTheme();
    const dropdownItems = useMemo(
        () =>
            actions.map((action) => ({
                label: action.label,
                icon: action.icon,
                color: action.color,
                style: action.style,
                onClick: action.onPress,
                testID: action.testID,
            })),
        [actions],
    );

    const dropdownComponent = useItemsDropdown({
        items: dropdownItems,
    });

    const dropdownProps = useDropdown({
        component: dropdownComponent,
    });
    const labelAlignStyle =
        onLeftAction || leftComponent ? styles.withLeftAction : styles.withoutLeftAction;

    // A single-word title (German compounds like "Stundenerfassung") cannot be broken between
    // words: rather than overflow, Android wraps a lone letter onto a second line. Those shrink
    // to fit on one line; multi-word titles keep their two-line layout.
    const shrinkToOneLine = useSmallLabel || (!!label && !label.trim().includes(" "));

    const onLayout = useCallback((e: LayoutChangeEvent) => {
        setHeaderHeight(e.nativeEvent.layout.height);
    }, []);

    const headerBackgroundColor = useSharedValue<ColorValue>("white");

    useEffect(() => {
        if (backgroundColor) {
            headerBackgroundColor.value = withTiming(backgroundColor as string);
        }
    }, [backgroundColor, headerBackgroundColor]);

    const animatedHeaderStyles = useAnimatedStyle(() => ({
        backgroundColor: headerBackgroundColor.value,
    }));

    return (
        <Animated.View style={[animatedHeaderStyles, styles.container]} onLayout={onLayout}>
            {leftComponent ? (
                <View style={styles.leftComponent}>{leftComponent}</View>
            ) : onLeftAction ? (
                <Pressable
                    id={id}
                    style={[styles.leftAction]}
                    onPress={!leftActionDisabled ? onLeftAction : null}
                >
                    {leftIcon ? (
                        <Icon
                            name={leftIcon}
                            style={{
                                marginTop: 2,
                                alignSelf: subLabel ? "flex-start" : undefined,
                            }}
                            color={leftActionDisabled ? colors.onSurfaceDisabled : colors.primary}
                            size={28}
                        />
                    ) : (
                        <Icon
                            name="back"
                            style={{ marginTop: 2, alignSelf: subLabel ? "flex-start" : undefined }}
                            size={28}
                            color={leftActionDisabled ? colors.onSurfaceDisabled : colors.primary}
                        />
                    )}
                </Pressable>
            ) : null}
            {label &&
                (subLabel ? (
                    <View style={[styles.labelView, labelAlignStyle]}>
                        <Text
                            style={styles.labelSmallText}
                            numberOfLines={1}
                            adjustsFontSizeToFit
                            minimumFontScale={MIN_LABEL_FONT_SCALE}
                        >
                            {label}
                        </Text>
                        <Text style={styles.subLabelText} numberOfLines={1}>
                            {subLabel}
                        </Text>
                    </View>
                ) : (
                    <View style={[styles.labelView, labelAlignStyle]}>
                        <Text
                            style={
                                useSmallLabel
                                    ? styles.labelSmallText
                                    : [styles.labelLargeText, isLarge && { lineHeight: 70 }]
                            }
                            // Titles that cannot wrap between words stay on a single line and
                            // shrink to fit rather than get truncated, down to
                            // MIN_LABEL_FONT_SCALE (see `shrinkToOneLine`).
                            numberOfLines={shrinkToOneLine ? 1 : 2}
                            adjustsFontSizeToFit={shrinkToOneLine}
                            minimumFontScale={shrinkToOneLine ? MIN_LABEL_FONT_SCALE : undefined}
                            ellipsizeMode="tail"
                        >
                            {label}
                        </Text>
                    </View>
                ))}
            {component && <View style={[styles.componentView]}>{component}</View>}
            {dynamicButtonConfig && useHeaderButton && (
                <Button
                    size="small"
                    leftIcon="add"
                    label={dynamicButtonConfig.shortTitle || dynamicButtonConfig.title}
                    onPress={dynamicButtonConfig.onClick}
                    buttonMode="light"
                    style={[animatedButtonStyles, { alignSelf: "center" }]}
                />
            )}
            <View style={[styles.actions, subLabel ? { height: 35 } : { marginTop: 5 }]}>
                {actions.length === 1 ? (
                    <>
                        {/* Spread (instead of destructured props) keeps the compiler from
                         * flagging the ref-typed `MenuAction.ref` field as a render-phase
                         * ref access — passing a ref through as a prop is the React 19
                         * intended pattern. */}
                        <ActionButton {...actions[0]} />
                        {actionButtonWithBadge && (
                            <View
                                style={{
                                    position: "absolute",
                                    top: -3,
                                    right: -1,
                                    height: 10,
                                    width: 10,
                                    borderRadius: 5,
                                    backgroundColor: colors.secondary,
                                    borderColor: colors.background,
                                    borderWidth: 1.5,
                                }}
                            />
                        )}
                    </>
                ) : actions.length > 1 ? (
                    <ActionButton
                        {...dropdownProps}
                        icon="menu"
                        testID="user-header-dropdown-menu"
                    />
                ) : null}
            </View>
        </Animated.View>
    );
};

const useStyles = (disableTopInset: boolean) => {
    const { colors, gridSize, marginSize, fonts } = useTheme();
    const insets = useSafeAreaInsets();
    const topInset = disableTopInset ? 0 : insets.top;
    return useMemo(
        () =>
            StyleSheet.create({
                container: {
                    paddingTop: topInset,
                    paddingHorizontal: marginSize,
                    width: "100%",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    minHeight: gridSize * 2 + topInset,
                },
                labelView: {
                    flexGrow: 1,
                    flexShrink: 1,
                    alignSelf: "center",
                    lineHeight: 30,
                },
                componentView: {
                    flexGrow: 1,
                    flexShrink: 1,
                    alignSelf: "flex-start",
                },
                withLeftAction: {
                    alignItems: "center",
                    // Kept symmetrical so the label stays optically centered between the left
                    // action and the actions menu, both `gridSize` wide. `marginSize` rather than
                    // `gridSize` to leave the title enough room before it gets truncated.
                    marginHorizontal: marginSize,
                },
                withoutLeftAction: { alignItems: "flex-start" },
                labelSmallText: {
                    ...fonts.titleMedium,
                    textAlign: "center",
                    lineHeight: 30,
                },
                labelLargeText: {
                    ...fonts.titleLarge,
                },
                subLabelText: {
                    ...fonts.bodySmall,
                    color: colors.onSurfaceDisabled,
                    lineHeight: 17,
                },
                headerButtonBordered: {
                    borderRadius: 3,
                    borderWidth: 2,
                    borderColor: colors.primary,
                },
                clientIconBorder: {
                    height: 24,
                    width: 24,
                    flexDirection: "row",
                    alignItems: "flex-end",
                    justifyContent: "center",
                },
                actions: {
                    width: gridSize,
                    paddingBottom: 4,
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                },
                screenTitle: {
                    fontSize: 30,
                    fontWeight: "bold",
                },
                leftAction: {
                    flexGrow: 0,
                    width: gridSize,
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                },
                leftComponent: {
                    flexGrow: 0,
                    flexShrink: 0,
                    flexDirection: "row",
                    alignItems: "center",
                },
            }),
        [
            colors.onSurfaceDisabled,
            colors.primary,
            fonts.bodySmall,
            fonts.titleLarge,
            fonts.titleMedium,
            gridSize,
            topInset,
            marginSize,
        ],
    );
};

export default ScreenHeader;
